import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3 } from 'lucide-react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { UtilisationTrend } from '@/types/utilisation';

interface PerformanceTrendsProps {
  trends: UtilisationTrend[];
}

export const PerformanceTrends: React.FC<PerformanceTrendsProps> = ({ trends }) => {
  // Get computed CSS colors
  const colors = useMemo(() => {
    if (typeof window === 'undefined') return {
      success: '#22c55e',
      neutral: '#d4d4d8',
      info: '#3b82f6',
      border: '#e4e4e7',
      mutedForeground: '#71717a',
      foreground: '#18181b',
      card: '#ffffff'
    };

    const style = getComputedStyle(document.documentElement);
    return {
      success: style.getPropertyValue('--success-500').trim() || 'oklch(0.50 0.1238 151.1)',
      neutral: style.getPropertyValue('--neutral-300').trim() || 'oklch(0.8 0.02 240)',
      info: style.getPropertyValue('--info-500').trim() || 'oklch(0.50 0.1562 249.9)',
      border: style.getPropertyValue('--border').trim() || 'oklch(0.9 0.02 240)',
      mutedForeground: style.getPropertyValue('--muted-foreground').trim() || 'oklch(0.45 0.02 240)',
      foreground: style.getPropertyValue('--foreground').trim() || 'oklch(0.09 0.02 240)',
      card: style.getPropertyValue('--card').trim() || 'oklch(1 0 0)'
    };
  }, []);

  // Prepare chart data for trends
  const trendChartData = useMemo(() => {
    return trends.map((trend) => ({
      week: format(trend.weekEnding, 'MMM d'),
      'Billable Hours': Number(trend.billableHours.toFixed(1)),
      'Non-Billable Hours': Number((trend.totalHours - trend.billableHours).toFixed(1)),
      'Utilisation %': Number((trend.utilisationRate * 100).toFixed(1)),
    }));
  }, [trends]);

  if (trends.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No trend data available for the selected period</p>
            <p className="text-sm mt-2">Try selecting a longer date range to see trends</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Weekly Trend</CardTitle>
        <CardDescription>Weekly hours breakdown with utilisation percentage overlay</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={trendChartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
            <XAxis 
              dataKey="week" 
              tick={{ fill: colors.mutedForeground, fontFamily: 'var(--font-sans)', fontSize: 12 }}
              stroke={colors.border}
            />
            <YAxis 
              yAxisId="left"
              tick={{ fill: colors.mutedForeground, fontFamily: 'var(--font-sans)', fontSize: 12 }}
              stroke={colors.border}
              label={{ 
                value: 'Hours', 
                angle: -90, 
                position: 'insideLeft', 
                style: { 
                  textAnchor: 'middle',
                  fill: colors.foreground,
                  fontFamily: 'var(--font-sans)',
                  fontSize: 12
                } 
              }}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              tick={{ fill: colors.mutedForeground, fontFamily: 'var(--font-sans)', fontSize: 12 }}
              stroke={colors.border}
              label={{ 
                value: 'Utilisation %', 
                angle: 90, 
                position: 'insideRight', 
                style: { 
                  textAnchor: 'middle',
                  fill: colors.foreground,
                  fontFamily: 'var(--font-sans)',
                  fontSize: 12
                } 
              }}
              domain={[0, 100]}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: colors.card,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                fontFamily: 'var(--font-sans)',
                fontSize: 12
              }}
              labelStyle={{
                color: colors.foreground,
                fontWeight: 600,
                marginBottom: 4
              }}
            />
            <Legend 
              wrapperStyle={{
                fontFamily: 'var(--font-sans)',
                fontSize: 12
              }}
            />
            <Bar 
              yAxisId="left"
              dataKey="Billable Hours" 
              stackId="hours"
              fill={colors.success}
              radius={[0, 0, 0, 0]}
            />
            <Bar 
              yAxisId="left"
              dataKey="Non-Billable Hours" 
              stackId="hours"
              fill={colors.neutral}
              radius={[4, 4, 0, 0]}
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="Utilisation %" 
              stroke={colors.info}
              strokeWidth={3}
              dot={{ r: 4, fill: colors.info, strokeWidth: 2, stroke: colors.card }}
              activeDot={{ r: 6 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
